/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  tituloBase,
  formatFechaHora
} from "../../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";

export default function Comunicaciones() {
  const [loader, setLoader] = useState(true);
  const [loaderBoton, setLoaderBoton] = useState(false);

  const [clientesConCertificado, setClientesConCertificado] = useState(false);
  const [certificadosTipo, setCertificadosTipo] = useState();

  // Direcciones
  const [direccionesFetched, setDireccionesFetched] = useState([]);
  const [direccionesManuales, setDireccionesManuales] = useState([]);
  const [direccionesExcluidas, setDireccionesExcluidas] = useState(new Set());

  // Sidebar
  const [sideBar, setSideBar] = useState(false);
  const [nuevaDireccion, setNuevaDireccion] = useState("");

  // Formulario de correo
  const [loaderEnvio, setLoaderEnvio] = useState(false);

  // Reenvío a los fallidos de un envío anterior (?reenvio=eventoId, desde
  // Enviados): precarga sus direcciones fallidas, el asunto, el cuerpo y el
  // Reply-To.
  const [searchParams] = useSearchParams();
  const reenvioDe = searchParams.get("reenvio");
  const [correoAnterior, setCorreoAnterior] = useState({});

  const toast = useRef(null);
  const direccionesActivasRef = useRef([]);

  useEffect(() => {
    document.title = "Comunicación masiva";

    // El formulario toma los valores iniciales al montarse, así que la
    // pantalla espera a tener también los datos del envío a repetir.
    let pendientes = reenvioDe ? 2 : 1;
    const terminar = () => {
      pendientes--;
      if (pendientes === 0) setLoader(false);
    };
    const mostrarError = (respuesta, detalle) =>
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: respuesta[0]?.msg || detalle,
        sticky: true,
      });

    accessAPI(
      "GET",
      "admin/config/certificadotipo",
      null,
      (respuesta) => {
        setCertificadosTipo(respuesta);
        terminar();
      },
      (respuesta) => {
        mostrarError(respuesta, "Error al cargar datos");
        terminar();
      }
    );

    if (reenvioDe) {
      accessAPI(
        "GET",
        `admin/comunicacion/mensajes/detalle/${reenvioDe}`,
        null,
        (evento) => {
          const parametros = evento.parametros || {};
          // Los envíos anteriores a guardar el cuerpo en los parámetros lo
          // tienen sólo en el registro de lo enviado.
          const cuerpo =
            parametros.cuerpo || evento.eventoprocesado?.[0]?.textoenviado?.texto;
          setCorreoAnterior({
            replyTo: parametros.replyTo,
            asunto: parametros.asunto,
            cuerpo,
          });
          setDireccionesManuales(
            (parametros.bccFallidos || []).map((email) => ({
              email,
              nombre: "",
              cliente: `Falló en el envío del ${formatFechaHora(evento.fechacreacion)}`,
            }))
          );
          terminar();
        },
        (respuesta) => {
          mostrarError(respuesta, "Error al cargar el envío a repetir");
          terminar();
        }
      );
    }

    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleCertificadoTipo(id, valor) {
    setCertificadosTipo((prev) =>
      prev.map((ct) => (ct.value === id ? { ...ct, checked: valor } : ct))
    );
  }

  function obtenerDirecciones() {
    setLoaderBoton(true);
    accessAPI(
      "POST",
      "admin/cliente/comunicaciones",
      { certificadosTipo, clientesConCertificado },
      (respuesta) => {
        setDireccionesFetched(respuesta.direcciones || []);
        setDireccionesExcluidas(new Set());
        setLoaderBoton(false);
      },
      (respuesta) => {
        setLoaderBoton(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al obtener direcciones",
          sticky: true,
        });
      }
    );
  }

  // Todas las direcciones combinadas (fetched + manuales)
  const todasLasDirecciones = [...direccionesFetched, ...direccionesManuales];

  // Direcciones activas (no excluidas)
  const direccionesActivas = todasLasDirecciones.filter(
    (d) => !direccionesExcluidas.has(d.email)
  );
  direccionesActivasRef.current = direccionesActivas;

  function excluirDireccion(email) {
    setDireccionesExcluidas((prev) => new Set([...prev, email]));
  }

  function restaurarDireccion(email) {
    setDireccionesExcluidas((prev) => {
      const next = new Set(prev);
      next.delete(email);
      return next;
    });
  }

  function agregarDireccionManual() {
    const email = nuevaDireccion.trim();
    if (!email) return;

    const emailRegEx = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegEx.test(email)) {
      toast.current?.show({
        severity: "warn",
        summary: "Formato inválido",
        detail: "Ingrese una dirección de correo válida",
        life: 3000,
      });
      return;
    }

    // Verifica que no exista ya
    const yaExiste = todasLasDirecciones.some((d) => d.email === email);
    if (yaExiste) {
      // Si estaba excluida, la restaura
      if (direccionesExcluidas.has(email)) {
        restaurarDireccion(email);
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Dirección duplicada",
          detail: "Esta dirección ya está en la lista",
          life: 3000,
        });
      }
      setNuevaDireccion("");
      return;
    }

    setDireccionesManuales((prev) => [
      ...prev,
      { email, nombre: "", cliente: "Agregada manualmente" },
    ]);
    setNuevaDireccion("");
  }

  function enviarCorreo(contenidoCampos) {
    const activas = direccionesActivasRef.current;
    if (activas.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Sin destinatarios",
        detail: "No hay direcciones para enviar",
        life: 3000,
      });
      return;
    }

    setLoaderEnvio(true);
    accessAPI(
      "POST",
      "admin/cliente/comunicaciones/enviar",
      {
        replyTo: contenidoCampos.replyTo.trim(),
        asunto: contenidoCampos.asunto.trim(),
        cuerpo: contenidoCampos.cuerpo.trim(),
        direcciones: activas.map((d) => d.email),
        reenvioDe: reenvioDe ? parseInt(reenvioDe) : null,
      },
      (respuesta) => {
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "success",
          summary: "Envío en curso",
          detail: respuesta.msg,
          life: 8000,
        });
      },
      (respuesta) => {
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al enviar el correo",
          sticky: true,
        });
      }
    );
  }

  const formularioCorreoConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "replyTo",
            respuesta: correoAnterior.replyTo,
            etiqueta: "Reply-To",
            tipoitemformularioid: 1,
            requerido: true,
          },
          {
            nombre: "asunto",
            respuesta: correoAnterior.asunto,
            etiqueta: "Asunto",
            tipoitemformularioid: 1,
            requerido: true,
          },
          {
            nombre: "cuerpo",
            respuesta: correoAnterior.cuerpo,
            etiqueta: "Cuerpo",
            tipoitemformularioid: 11,
            requerido: true,
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: "Enviar correo",
        callback: enviarCorreo,
        validar: true,
        confirmacion: "¿Confirmar el envío del correo?",
        azul: true,
      },
    ],
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBar}
        onHide={() => setSideBar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar direcciones</Titulo>

        <div css={{ marginBottom: "1.5rem" }}>
          <label
            css={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem" }}
          >
            Agregar dirección
          </label>
          <div css={{ display: "flex", gap: "0.5rem" }}>
            <InputText
              value={nuevaDireccion}
              onChange={(e) => setNuevaDireccion(e.target.value)}
              placeholder="email@ejemplo.com"
              css={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") agregarDireccionManual();
              }}
            />
            <Boton verde callback={agregarDireccionManual}>
              <i className="pi pi-plus"></i>
            </Boton>
          </div>
        </div>

        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          {todasLasDirecciones.length === 0 && (
            <p css={{ color: "var(--grisTextos)" }}>
              No hay direcciones cargadas.
            </p>
          )}
          {todasLasDirecciones.map((dir) => {
            const excluida = direccionesExcluidas.has(dir.email);
            return (
              <div
                key={dir.email}
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.5rem 0.75rem",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: excluida ? "#f9f9f9" : "white",
                  textDecoration: excluida ? "line-through" : "none",
                  opacity: excluida ? 0.6 : 1,
                }}
              >
                <div css={{ flex: 1, minWidth: 0 }}>
                  <div
                    css={{
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dir.email}
                  </div>
                  <div css={{ fontSize: "0.75rem", color: "#666" }}>
                    {dir.nombre && `${dir.nombre} - `}
                    {dir.cliente}
                  </div>
                </div>
                {excluida ? (
                  <Boton
                    texto
                    chico
                    verde
                    callback={() => restaurarDireccion(dir.email)}
                  >
                    <i className="pi pi-plus"></i>
                  </Boton>
                ) : (
                  <Boton
                    texto
                    chico
                    rojo
                    callback={() => excluirDireccion(dir.email)}
                  >
                    <i className="pi pi-times"></i>
                  </Boton>
                )}
              </div>
            );
          })}
        </div>
      </Sidebar>

      {loader && (
        <div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "2em",
              marginBottom: "1em",
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
          </div>
          <div className="grid">
            <div className="col-12 lg:col-4">
              <Card>
                <Skeleton
                  width="200px"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Skeleton
                        width="3rem"
                        height="1.75rem"
                        borderRadius="14px"
                      />
                      <Skeleton width="120px" height="1rem" />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
            <div className="col-12 lg:col-8">
              <Card>
                <Skeleton
                  width="200px"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <Skeleton width="100%" height="2.5rem" css={{ marginBottom: "1rem" }} />
                <Skeleton width="100%" height="2.5rem" css={{ marginBottom: "1rem" }} />
                <Skeleton width="100%" height="8rem" />
              </Card>
            </div>
          </div>
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            botones={
              <Boton transparente uri="/comunicacion/enviados">
                Ver envíos
              </Boton>
            }
          >
            Comunicación masiva
          </Titulo>

          <div className="grid">
            <div className="col-12 lg:col-4">
              <Card>
                <Titulo>Filtrar por certificado tipo</Titulo>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  {certificadosTipo &&
                    certificadosTipo.map((ct) => (
                      <Boton
                        key={ct.value}
                        interruptor
                        interruptorActivo={ct.checked || false}
                        callback={(valor) =>
                          toggleCertificadoTipo(ct.value, valor)
                        }
                      >
                        {ct.etiqueta}
                      </Boton>
                    ))}
                </div>

                <Titulo>Filtrar por certificado vigente</Titulo>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                    marginBottom: "1.5rem",
                  }}
                >
                  <Boton
                    interruptor
                    interruptorActivo={clientesConCertificado}
                    callback={(valor) => setClientesConCertificado(valor)}
                  >
                    Con certificados emitidos
                  </Boton>
                </div>

                <Boton callback={obtenerDirecciones} loader={loaderBoton} azul>
                  Obtener direcciones
                </Boton>
              </Card>
            </div>

            <div className="col-12 lg:col-8">
              <Card>
                <Titulo>Enviar correo</Titulo>

                {todasLasDirecciones.length > 0 ? (
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <span css={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                      {direccionesActivas.length} dirección
                      {direccionesActivas.length !== 1 ? "es" : ""}
                      {direccionesExcluidas.size > 0 &&
                        ` (${direccionesExcluidas.size} excluida${direccionesExcluidas.size !== 1 ? "s" : ""})`}
                    </span>
                    <Boton texto azul chico callback={() => setSideBar(true)}>
                      <i className="pi pi-pencil" css={{ marginRight: "0.25rem" }}></i>
                      Editar
                    </Boton>
                  </div>
                ) : (
                  <p css={{ color: "var(--grisTextos)", marginBottom: "1.5rem" }}>
                    Seleccioná los filtros y presioná "Obtener direcciones" para
                    cargar destinatarios.
                  </p>
                )}

                <Formulario
                  config={formularioCorreoConfig}
                  loader={loaderEnvio}
                  formularioEnCuerpo
                />
              </Card>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
